import { openDB, deleteDB } from "idb";
import { COLLECTIONS } from "../const";

export const DB_NAME = "SanoftPOS";
export const DB_VERSION = 26;

export async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      if (oldVersion < 13 && db.objectStoreNames.contains("sales")) {
        db.deleteObjectStore("sales");
      }

      if (oldVersion < 17 && db.objectStoreNames.contains("vendors")) {
        db.deleteObjectStore("vendors");
      }

      Object.values(COLLECTIONS).forEach(({ name, keyPath, autoIncrement, indexes }) => {
        let store;
        if (!db.objectStoreNames.contains(name)) {
          store = db.createObjectStore(name, { keyPath, autoIncrement });
        } else {
          store = transaction.objectStore(name);
        }

        if (indexes && Array.isArray(indexes)) {
          indexes.forEach(({ name: indexName, keyPath: indexPath, options }) => {
            if (!store.indexNames.contains(indexName)) {
              store.createIndex(indexName, indexPath || indexName, options);
            }
          });
        }
      });
    },
  });
}

export function handleDatabaseError(error, context = "database operation") {
  console.error(`Database error during ${context}:`, error);
  const message = error?.message || "";
  const name = error?.name || "";

  if (
    name === "QuotaExceededError" ||
    message.includes("QuotaExceededError") ||
    message.includes("full disk") ||
    message.includes("backing store") ||
    (name === "AbortError" && (message.includes("aborted") || message.includes("fulfilled")))
  ) {
    throw new Error("Storage quota exceeded or your device's disk space is full");
  }

  throw error;
}

const IndexDBAdapter = {
  async add(store, data) {
    try {
      const db = await getDB();
      return await db.add(store, data);
    } catch (error) {
      handleDatabaseError(error, "adding data");
    }
  },

  async update(store, key, data) {
    try {
      const db = await getDB();
      data[db.transaction(store).store.keyPath] = key;
      return await db.put(store, data);
    } catch (error) {
      handleDatabaseError(error, "updating data");
    }
  },

  async delete(store, id) {
    try {
      const db = await getDB();
      return await db.delete(store, id);
    } catch (error) {
      handleDatabaseError(error, "deleting data");
    }
  },

  async clear(store) {
    try {
      const db = await getDB();
      return await db.clear(store);
    } catch (error) {
      handleDatabaseError(error, "clearing data");
    }
  },

  async get(store, id) {
    try {
      const db = await getDB();
      return await db.get(store, id);
    } catch (error) {
      handleDatabaseError(error, "getting data");
    }
  },

  async getAll(store) {
    try {
      const db = await getDB();
      return await db.getAll(store);
    } catch (error) {
      handleDatabaseError(error, "getting all data");
    }
  },

  async addAll(store, list) {
    try {
      const db = await getDB();
      const tx = db.transaction(store, "readwrite");
      await tx.store.clear();
      for (const item of list) {
        tx.store.put(item);
      }
      return await tx.done;
    } catch (error) {
      handleDatabaseError(error, "adding all data");
    }
  },

  async filter(store, query) {
    try {
      const all = await this.getAll(store);

      if (typeof query === "function") {
        return all.filter(query);
      }

      return all.filter((item) => {
        return Object.entries(query).every(([key, value]) => {
          const keys = key.split(".");
          let field = item;
          for (const k of keys) {
            field = field?.[k];
          }

          if (!isNaN(field) && !isNaN(value)) {
            return Number(field) === Number(value);
          }

          if (Array.isArray(value)) {
            return value.includes(field);
          }

          return field === value;
        });
      });
    } catch (error) {
      handleDatabaseError(error, "filtering data");
    }
  },

  async runTransaction(storeNames, callback, context = "database transaction", mode = "readwrite") {
    try {
      const dbInstance = await getDB();
      const tx = dbInstance.transaction(storeNames, mode);
      const stores = {};
      for (const name of storeNames) {
        stores[name] = tx.objectStore(name);
      }
      const result = await callback(stores, tx);
      await tx.done;
      return result;
    } catch (error) {
      handleDatabaseError(error, context);
    }
  },

  async deleteDatabase() {
    try {
      const database = await getDB();
      const storeNames = Array.from(database.objectStoreNames);
      if (storeNames.length > 0) {
        const tx = database.transaction(storeNames, "readwrite");
        for (const storeName of storeNames) {
          tx.objectStore(storeName).clear();
        }
        await tx.done;
      }
      database.close();
    } catch (err) {
      console.warn("Could not clear object stores before deleting database:", err);
    }

    try {
      return await deleteDB(DB_NAME, {
        blocked() {
          window.location.reload();
        },
      });
    } catch (error) {
      handleDatabaseError(error, "deleting database");
    }
  },
};

export default IndexDBAdapter;
